// [PURPOSE]: Resolve a "value at this level" lookup against a data-block
//   table of the shape `[{ atLevel, value }, ...]`.
//
// [WHY]: Phase B / #042. Class-feature scaling (Barbarian rage damage,
//   Battle Master superiority die, Monk Martial Arts die, etc.) used to
//   live as hardcoded `if (level >= N) return X` ladders inside the
//   resolver and assorted UI components. Strings go against god — and so
//   do hardcoded ladders. Each scaling moves to a `*ByLevel` data block
//   on the owning entity; this helper does the lookup.
//
// [SHAPE]:
//   const rageDamageByLevel = [
//     { atLevel: 1, value: 2 },
//     { atLevel: 9, value: 3 },
//     { atLevel: 16, value: 4 },
//   ];
//
//   resolveByLevelTable(rageDamageByLevel, 9)  // → 3
//   resolveByLevelTable(rageDamageByLevel, 0)  // → null  (below floor)
//
// [INTEGRATION]: Used by `effectRegistry._getValue` for the
//   'rage_damage_bonus' branch (#042). Future commits in the carry-over
//   campaign reuse it for Battle Master superiority die (#043) and any
//   other `*ByLevel` block we add.

/**
 * Find the largest entry in a level table whose `atLevel` is <= the
 * given level. Returns `entry.value` (not the entry itself) so callers
 * can drop the result straight into a damage formula or die string.
 *
 * @param {Array<{atLevel:number, value:any}>|null|undefined} table
 * @param {number} level
 * @returns {any|null} The matching entry's `value`, or null if no entry
 *   qualifies (or table is invalid).
 */
export function resolveByLevelTable(table, level) {
  if (!Array.isArray(table) || table.length === 0) return null;
  if (typeof level !== 'number' || Number.isNaN(level)) return null;

  let best = null;
  for (const entry of table) {
    if (!entry || typeof entry.atLevel !== 'number') continue;
    if (entry.atLevel > level) continue;
    if (best === null || entry.atLevel > best.atLevel) {
      best = entry;
    }
  }
  return best ? best.value : null;
}
